import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Star, Mail, CheckCircle, TrendingUp, Loader2, XCircle, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReviewTracker({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/admin/review-tracker`, { headers }),
      axios.get(`${API}/api/admin/review-analytics`, { headers }).catch(() => ({ data: { months: [] } })),
    ]).then(([trackerRes, analyticsRes]) => {
      setData(trackerRes.data);
      setAnalytics(analyticsRes.data);
    }).catch(() => {})
    .finally(() => setLoading(false));
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const toggleReviewed = async (logId) => {
    setTogglingId(logId);
    try {
      const res = await axios.put(`${API}/api/admin/review-tracker/${logId}/mark-reviewed`, {}, { headers });
      setData(prev => ({
        ...prev,
        total_reviewed: prev.total_reviewed + (res.data.reviewed ? 1 : -1),
        response_rate: prev.total_sent > 0
          ? Math.round((prev.total_reviewed + (res.data.reviewed ? 1 : -1)) / prev.total_sent * 1000) / 10
          : 0,
        logs: prev.logs.map(l => l.id === logId ? { ...l, reviewed: res.data.reviewed } : l)
      }));
      toast.success(res.data.reviewed ? 'Marked as reviewed' : 'Unmarked');
    } catch { toast.error('Failed to update'); }
    finally { setTogglingId(null); }
  };

  if (loading || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-neutral-400">
          <div className="animate-pulse">Loading review tracker...</div>
        </CardContent>
      </Card>
    );
  }

  if (data.total_sent === 0) {
    return (
      <Card data-testid="review-tracker">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> Review Tracker</CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center text-neutral-400 text-sm">
          No review requests sent yet. They are sent automatically 7 days after installation.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="review-tracker">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard icon={Mail} label="Total Sent" value={data.total_sent} color="blue" />
        <StatCard icon={Star} label="This Week" value={data.sent_this_week} color="amber" />
        <StatCard icon={CheckCircle} label="Reviewed" value={data.total_reviewed} color="green" />
        <StatCard icon={TrendingUp} label="Response Rate" value={`${data.response_rate}%`} color="purple" />
      </div>

      {/* Analytics Chart */}
      {analytics?.months?.some(m => m.sent > 0) && (
        <Card data-testid="review-analytics-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-500" /> Monthly Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analytics.months} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sent" name="Requests Sent" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="reviewed" name="Reviewed" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Activity Log */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" /> Review Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-72 overflow-y-auto divide-y">
            {data.logs.map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors" data-testid={`review-log-${entry.id}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${entry.reviewed ? 'bg-green-100' : 'bg-neutral-100'}`}>
                  {entry.reviewed
                    ? <CheckCircle className="w-4 h-4 text-green-600" />
                    : <Mail className="w-4 h-4 text-neutral-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-800 truncate">
                    {entry.customer_name || entry.email}
                    <span className="text-neutral-400 font-normal ml-2">SO #{entry.so_number}</span>
                  </p>
                  <p className="text-xs text-neutral-500">
                    Sent {new Date(entry.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {entry.reviewed && <span className="ml-2 text-green-600 font-medium">Reviewed</span>}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleReviewed(entry.id)}
                  disabled={togglingId === entry.id}
                  className={entry.reviewed
                    ? 'text-red-500 hover:text-red-600 hover:border-red-300 border-red-200'
                    : 'text-green-600 hover:text-green-700 hover:border-green-300 border-green-200'}
                  data-testid={`toggle-reviewed-${entry.id}`}
                >
                  {togglingId === entry.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : entry.reviewed
                      ? <><XCircle className="w-3.5 h-3.5 mr-1" /> Unmark</>
                      : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Reviewed</>}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs text-neutral-500">{label}</p>
          <p className="text-lg font-bold text-neutral-800">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
